import { useEffect, useState, useCallback } from 'react';
import type { Goal, GoalCalc, GoalContribution } from '../../../hooks/useGoals';

interface GoalDetailModalProps {
  open: boolean;
  goal: Goal | null;
  calc: GoalCalc | null;
  onClose: () => void;
  onEdit: (goal: Goal) => void;
  onAddContribution: (goal: Goal) => void;
  fetchContributions: (goalId: string) => Promise<GoalContribution[]>;
  removeContribution: (contribution: GoalContribution) => Promise<{ error: string | null }>;
}

const PACE_CONFIG = {
  completed: { label: 'Concluída', color: 'text-emerald-700', bg: 'bg-emerald-100', icon: 'ri-check-line' },
  on_track: { label: 'No ritmo', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: 'ri-checkbox-circle-line' },
  at_risk: { label: 'Em risco', color: 'text-amber-600', bg: 'bg-amber-50', icon: 'ri-error-warning-line' },
  behind: { label: 'Atrasada', color: 'text-red-600', bg: 'bg-red-50', icon: 'ri-time-line' },
  overdue: { label: 'Vencida', color: 'text-red-700', bg: 'bg-red-100', icon: 'ri-alarm-warning-line' },
  no_date: { label: 'Sem prazo', color: 'text-gray-500', bg: 'bg-gray-100', icon: 'ri-calendar-line' },
};

const PRIORITY_LABELS: Record<string, string> = { high: 'Alta', medium: 'Média', low: 'Baixa' };
const PRIORITY_COLOR: Record<string, string> = {
  high: 'text-red-600 bg-red-50',
  medium: 'text-amber-600 bg-amber-50',
  low: 'text-gray-500 bg-gray-100',
};

const STATUS_LABELS: Record<string, string> = { active: 'Ativa', paused: 'Pausada', completed: 'Concluída' };

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d: string) =>
  new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
const fmtShort = (d: string) =>
  new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

const SOURCE_ICON: Record<string, string> = {
  account: 'ri-bank-line',
  debit_card: 'ri-bank-card-line',
  manual: 'ri-pencil-line',
};
const SOURCE_LABEL: Record<string, string> = {
  account: 'Conta',
  debit_card: 'Cartão débito',
  manual: 'Manual',
};

export const GoalDetailModal = ({
  open, goal, calc, onClose, onEdit, onAddContribution, fetchContributions, removeContribution,
}: GoalDetailModalProps) => {
  const [contributions, setContributions] = useState<GoalContribution[]>([]);
  const [loadingContribs, setLoadingContribs] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');

  const reloadContributions = useCallback(async (goalId: string) => {
    setLoadingContribs(true);
    const data = await fetchContributions(goalId);
    setContributions(data);
    setLoadingContribs(false);
  }, [fetchContributions]);

  useEffect(() => {
    if (open && goal) {
      setContributions([]);
      setConfirmRemoveId(null);
      setRemoveError('');
      reloadContributions(goal.id);
    }
  }, [open, goal, reloadContributions]);

  if (!open || !goal || !calc) return null;

  const pace = PACE_CONFIG[calc.pace];

  const handleRemove = async (contribution: GoalContribution) => {
    setRemoving(true);
    setRemoveError('');
    const { error } = await removeContribution(contribution);
    if (error) {
      setRemoveError('Erro ao estornar aporte. Tente novamente.');
      setRemoving(false);
      return;
    }
    setConfirmRemoveId(null);
    setRemoving(false);
    if (goal) await reloadContributions(goal.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-lg w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div
          className="relative overflow-hidden flex-shrink-0 px-6 py-5"
          style={{ background: `linear-gradient(135deg, ${goal.color}22, ${goal.color}10)`, borderBottom: `2px solid ${goal.color}30` }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: goal.color + '25' }}
              >
                <i className={`${goal.icon || 'ri-trophy-line'} text-2xl`} style={{ color: goal.color }} />
              </div>
              <div>
                <p className="text-base font-700 text-gray-900">{goal.name}</p>
                {goal.description && <p className="text-xs text-gray-500 mt-0.5 max-w-xs">{goal.description}</p>}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${PRIORITY_COLOR[goal.priority]}`}>
                    {PRIORITY_LABELS[goal.priority]}
                  </span>
                  <span className="text-xs text-gray-400">{STATUS_LABELS[goal.status]}</span>
                  <span className={`flex items-center gap-1 text-xs font-600 px-2 py-0.5 rounded-full ${pace.bg} ${pace.color}`}>
                    <i className={`${pace.icon} text-xs`} />
                    {pace.label}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/60 text-gray-400 cursor-pointer flex-shrink-0">
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Progress section */}
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-600 text-gray-500">Progresso</span>
              <span className="text-sm font-700" style={{ color: goal.color }}>{calc.progress.toFixed(1)}%</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-3">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${calc.progress}%`, backgroundColor: goal.color }}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-0.5">Acumulado</p>
                <p className="text-sm font-700 text-gray-800">{fmt(goal.current_amount)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-0.5">Meta</p>
                <p className="text-sm font-700 text-gray-800">{fmt(goal.target_amount)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-0.5">Faltam</p>
                <p className="text-sm font-700 text-red-500">{fmt(calc.remaining)}</p>
              </div>
            </div>
          </div>

          {/* Key metrics */}
          <div className="px-6 py-4 border-b border-gray-100">
            <p className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-3">Métricas</p>
            <div className="grid grid-cols-2 gap-3">
              {goal.target_date && (
                <div className="bg-gray-50 rounded-xl px-3 py-2.5">
                  <p className="text-xs text-gray-400">Data alvo</p>
                  <p className="text-sm font-700 text-gray-800 mt-0.5">{fmtDate(goal.target_date)}</p>
                </div>
              )}
              {calc.monthsRemaining !== null && (
                <div className="bg-gray-50 rounded-xl px-3 py-2.5">
                  <p className="text-xs text-gray-400">Tempo restante</p>
                  <p className="text-sm font-700 text-gray-800 mt-0.5">
                    {calc.monthsRemaining < 1
                      ? 'Menos de 1 mês'
                      : `${Math.round(calc.monthsRemaining)} ${Math.round(calc.monthsRemaining) === 1 ? 'mês' : 'meses'}`}
                  </p>
                </div>
              )}
              {calc.monthlyNeeded !== null && calc.monthlyNeeded > 0 && (
                <div className="bg-emerald-50 rounded-xl px-3 py-2.5">
                  <p className="text-xs text-emerald-600">Aporte ideal/mês</p>
                  <p className="text-sm font-700 text-emerald-700 mt-0.5">{fmt(calc.monthlyNeeded)}</p>
                </div>
              )}
              <div className="bg-gray-50 rounded-xl px-3 py-2.5">
                <p className="text-xs text-gray-400">Aportes registrados</p>
                <p className="text-sm font-700 text-gray-800 mt-0.5">{contributions.length}</p>
              </div>
            </div>

            {calc.pace === 'on_track' && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-emerald-50 rounded-xl border border-emerald-100">
                <i className="ri-checkbox-circle-fill text-emerald-500" />
                <p className="text-xs text-emerald-700 font-500">Você está no ritmo certo para atingir essa meta no prazo!</p>
              </div>
            )}
            {(calc.pace === 'at_risk' || calc.pace === 'behind') && calc.monthlyNeeded && (
              <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-amber-50 rounded-xl border border-amber-100">
                <i className="ri-lightbulb-line text-amber-500 mt-0.5" />
                <p className="text-xs text-amber-700 font-500">
                  Para atingir essa meta no prazo, guarde pelo menos <strong>{fmt(calc.monthlyNeeded)}/mês</strong>.
                </p>
              </div>
            )}
            {calc.pace === 'overdue' && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-red-50 rounded-xl border border-red-100">
                <i className="ri-alarm-warning-line text-red-500" />
                <p className="text-xs text-red-700 font-500">A data alvo passou. Atualize a meta ou reforce os aportes.</p>
              </div>
            )}
          </div>

          {/* Contributions history */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-600 text-gray-500 uppercase tracking-wide">Histórico de aportes</p>
              <button
                onClick={() => onAddContribution(goal)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-600 rounded-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" />Registrar aporte
              </button>
            </div>

            {removeError && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">
                <i className="ri-error-warning-line" />{removeError}
              </div>
            )}

            {loadingContribs ? (
              <div className="space-y-2">
                {[1, 2].map(i => <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />)}
              </div>
            ) : contributions.length === 0 ? (
              <div className="text-center py-6">
                <div className="w-9 h-9 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <i className="ri-coin-line text-gray-400" />
                </div>
                <p className="text-xs text-gray-400">Nenhum aporte registrado ainda.</p>
                <p className="text-xs text-gray-300 mt-0.5">Registre seu primeiro aporte para começar a acompanhar a evolução.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {contributions.map(c => {
                  const srcType = (c.source_type ?? 'manual') as string;
                  const srcIcon = SOURCE_ICON[srcType] ?? 'ri-pencil-line';
                  const srcLabel = SOURCE_LABEL[srcType] ?? 'Manual';
                  const displayDate = c.contribution_date ?? c.created_at;
                  const isConfirming = confirmRemoveId === c.id;

                  return (
                    <div key={c.id} className="rounded-xl overflow-hidden border border-transparent">
                      {/* Main row */}
                      <div className={`flex items-center justify-between py-2.5 px-3 gap-3 transition-colors ${isConfirming ? 'bg-red-50' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                            style={{ backgroundColor: isConfirming ? '#fef2f2' : goal.color + '20' }}>
                            <i className={`${srcIcon} text-xs`} style={{ color: isConfirming ? '#ef4444' : goal.color }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-600 text-gray-700 truncate">
                              {c.note || 'Aporte'}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-400">{fmtShort(displayDate)}</span>
                              <span className="text-xs text-gray-300">·</span>
                              <span className="text-xs text-gray-400 font-500">{srcLabel}</span>
                              {c.observation && (
                                <>
                                  <span className="text-xs text-gray-300">·</span>
                                  <span className="text-xs text-gray-400 truncate max-w-[120px]" title={c.observation}>{c.observation}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <p className="text-sm font-700 text-emerald-600">+{fmt(c.amount)}</p>
                          <button
                            onClick={() => {
                              setConfirmRemoveId(isConfirming ? null : c.id);
                              setRemoveError('');
                            }}
                            title="Estornar aporte"
                            className={`w-6 h-6 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                              isConfirming
                                ? 'bg-red-100 text-red-500'
                                : 'text-gray-300 hover:text-red-400 hover:bg-red-50'
                            }`}
                          >
                            <i className="ri-arrow-go-back-line text-xs" />
                          </button>
                        </div>
                      </div>

                      {/* Inline confirmation strip */}
                      {isConfirming && (
                        <div className="bg-red-50 border-t border-red-100 px-3 py-2.5">
                          <p className="text-xs text-red-700 font-600 mb-2 flex items-center gap-1.5">
                            <i className="ri-error-warning-line" />
                            Estornar {fmt(c.amount)} de &quot;{c.note || 'Aporte'}&quot;?
                            {c.source_type !== 'manual' && (
                              <span className="font-400 text-red-500">O valor voltará para a conta de origem.</span>
                            )}
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setConfirmRemoveId(null); setRemoveError(''); }}
                              className="flex-1 py-1.5 text-xs font-600 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={() => handleRemove(c)}
                              disabled={removing}
                              className="flex-1 py-1.5 text-xs font-600 text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 rounded-lg cursor-pointer whitespace-nowrap"
                            >
                              {removing ? 'Estornando...' : 'Confirmar estorno'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3 flex-shrink-0">
          <p className="text-xs text-gray-400 flex-1">
            Criada em {fmtShort(goal.created_at)}
          </p>
          <button
            onClick={() => { onClose(); onEdit(goal); }}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line" />Editar meta
          </button>
        </div>
      </div>
    </div>
  );
};
